import { useEffect, useState } from "react";
import type { Nullable } from "../../@types/generic";
import { getWindow } from "../../helpers/getWindow";
import { usePageParams } from "../../hooks/usePageParams";

type UseHeaderScroll = {
    ref: React.RefObject<Nullable<HTMLDivElement>>;
    styles: Record<string, string>;
};

export const useHeaderScroll = ({ ref, styles }: UseHeaderScroll): void => {
    const { pageType } = usePageParams();
    const window = getWindow();

    useEffect(() => {
        let lastScrollY = window?.scrollY ?? 0;
        const handleScroll = (): void => {
            if (ref.current && !pageType?.isLivecamDetailPage) {
                const currentScrollY = window?.scrollY ?? 0;
                // reset translate on initial position and when scrolling up, otherwise hide

                if (
                    currentScrollY === 0 ||
                    (currentScrollY > 0 && currentScrollY < lastScrollY)
                ) {
                    ref.current.classList.remove(styles.isHidden ?? "");
                    ref.current.classList.remove(styles.hasShadow ?? "");
                } else if (currentScrollY > ref.current.offsetHeight) {
                    ref.current.classList.add(styles.isHidden ?? "");
                }

                // add box shadow when scrolling down
                if (
                    currentScrollY > 0 &&
                    !ref.current.classList.contains(styles.isHidden ?? "")
                ) {
                    ref.current.classList.add(styles.hasShadow ?? "");
                } else {
                    ref.current.classList.remove(styles.hasShadow ?? "");
                }
                lastScrollY = currentScrollY;
            }
        };

        if (!pageType?.isLivecamDetailPage) {
            window?.addEventListener("scroll", handleScroll, { passive: true });
        }

        return (): void => {
            window?.removeEventListener("scroll", handleScroll);
        };
    }, [pageType?.isLivecamDetailPage, window, ref, styles]);
};

type UseStickyHeader = {
    headerRef: React.RefObject<Nullable<HTMLElement>>;
};

type StickyHeaderState = {
    isSticky: boolean;
    isMenuOpen: boolean;
    toggleMenu: () => void;
};

// once scrolled past the header's own (expanded) height, it stays fixed and
// dark until scrolled back up to the top again - no hide/show on direction
export const useStickyHeader = ({
    headerRef,
}: UseStickyHeader): StickyHeaderState => {
    const window = getWindow();
    const [isSticky, setIsSticky] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        if (!headerRef.current) {
            return undefined;
        }

        const threshold = headerRef.current.offsetHeight;

        const handleScroll = (): void => {
            const currentScrollY = window?.scrollY ?? 0;
            const shouldBeSticky = currentScrollY > threshold;
            setIsSticky(shouldBeSticky);
            if (!shouldBeSticky) {
                // collapse the re-opened nav again once scrolled back to the top
                setIsMenuOpen(false);
            }
        };

        handleScroll();
        window?.addEventListener("scroll", handleScroll, { passive: true });

        return (): void => {
            window?.removeEventListener("scroll", handleScroll);
        };
    }, [window, headerRef]);

    const toggleMenu = (): void => setIsMenuOpen((current) => !current);

    return { isSticky, isMenuOpen, toggleMenu };
};
